import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AuthLayout from '../layouts/AuthLayout';
import Layout from '../layouts/Layout';
import EditSegment from '../pages/Audience/EditSegment';
import SegmentSelection from '../pages/Audience/SegmentSelection';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Auth imports
import AcceptInvite from '../pages/Auth/AcceptInvite';
import Callback from '../pages/Auth/Callback';
import Login from '../pages/Auth/Login';
import Signup from '../pages/Auth/Signup';

// Other page imports
import Analytics from '../pages/Analytics';
import Audience from '../pages/Audience';
import AddAudience from '../pages/Audience/AddAudience';
import EditContact from '../pages/Audience/EditContact';
import ViewAudience from '../pages/Audience/ViewAudience';
import Automations from '../pages/Automations';
import CreateAutomation from '../pages/Automations/CreateAutomation';
import EditAutomation from '../pages/Automations/EditAutomation';
import ViewAutomation from '../pages/Automations/ViewAutomation';
import Campaigns from '../pages/Campaigns';
import CreateCampaign from '../pages/Campaigns/CreateCampaign';
import ViewCampaign from '../pages/Campaigns/ViewCampaign';
import Dashboard from '../pages/Dashboard';
import Profile from '../pages/Profile';
import Reports from '../pages/Reports';
import ReportsViewAutomation from '../pages/Reports/ReportsViewAutomation';
import ReportsViewCampaign from '../pages/Reports/ReportsViewCampaign';
import ReportsViewRecipient from '../pages/Reports/ReportsViewRecipient';
import Settings from '../pages/Settings';
import IntegrationDetails from '../pages/Settings/IntegrationDetails';
import Team from '../pages/Team';
import MemberDetails from '../pages/Team/MemberDetails';
import Templates from '../pages/Templates';
import CreateTemplatePage from '../pages/Templates/CreateTemplatePage';
import Segments from '../pages/Audience/Segments';

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path='/' element={<Navigate to='/login' replace />} />

      {/* Public routes */}
      <Route element={<PublicRoute />}>
        <Route
          path='/login'
          element={
            <AuthLayout
              title={
                <>
                  Sign In to <span className='text-orange-500'>Auto</span>
                  <span>Path</span>
                </>
              }
            >
              <Login />
            </AuthLayout>
          }
        />
        <Route
          path='/register'
          element={
            <AuthLayout
              title={
                <>
                  Sign Up to get into{' '}
                  <span className='text-orange-500'>Auto</span>
                  <span>Path</span>
                </>
              }
            >
              <Signup />
            </AuthLayout>
          }
        />
        {/* ✅ Callback route for OAuth redirect */}
        <Route path='/callback' element={<Callback />} />

        {/* ✅ NEW: Accept Invite route */}
        <Route path='/accept-invite' element={<AcceptInvite />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path='/dashboard' element={<Dashboard />} />
          <Route path='/campaigns' element={<Campaigns />} />
          <Route path='/campaigns/create' element={<CreateCampaign />} />
          <Route path='/campaigns/edit/:id' element={<CreateCampaign />} />
          <Route path='/campaigns/view/:id' element={<ViewCampaign />} />

          {/* Audience routes */}
          <Route path='/audience' element={<Audience />} />
          <Route path='/audience/segments' element={<SegmentSelection />} />
          <Route path='/audience/segments/add/:segment' element={<AddAudience />} />
          <Route path='/audience/segments/add' element={<AddAudience />} />
          <Route path='/audience/segments/edit/:segment' element={<EditSegment />} />
          <Route path='/audience/view/:id' element={<ViewAudience />} />
          <Route path='/audience/edit/:contactId' element={<EditContact />} />
          <Route path='/segments' element={<Segments />} />


          {/* Automation routes */}
          <Route path='/automations' element={<Automations />} />
          <Route path='/automations/create' element={<CreateAutomation />} />
          <Route path='/automations/view/:id' element={<ViewAutomation />} />
          <Route path='/automations/edit/:id' element={<EditAutomation />} />

          {/* Template routes */}
          <Route path='/templates' element={<Templates />} />
          <Route path='/templates/create' element={<CreateTemplatePage />} />
          <Route path='/templates/:id' element={<CreateTemplatePage />} />

          {/* Analytics and Reports */}
          <Route path='/analytics' element={<Analytics />} />
          <Route path='/reports' element={<Reports />} />
          <Route path='/reports/campaigns/view/:id' element={<ReportsViewCampaign />} />
          <Route path='/reports/automations/view/:id' element={<ReportsViewAutomation />} />
          <Route path='/reports/automations/view/:id/recipients/:id/' element={<ReportsViewRecipient />} />
          <Route path='/reports/campaigns/view/:id/recipients/:id/' element={<ReportsViewRecipient />} />

          {/* Team and Settings */}
          <Route path='/team' element={<Team />} />
          <Route path='/team/:id' element={<MemberDetails />} />
          <Route path='/settings' element={<Settings />} />
          <Route path='/settings/integrations/:id' element={<IntegrationDetails />} />

          {/* Profile */}
          <Route path='/profile' element={<Profile />} />
        </Route>
      </Route>

      {/* Catch-all - redirect to dashboard */}
      <Route path='*' element={<Navigate to='/dashboard' replace />} />
    </Routes>
  );
};

export default AppRoutes;